// useCountryData Hook
// Fetches complete country overview with all key indicators

import { useState, useEffect, useCallback, useMemo } from 'react';
import { dataService } from '../services';
import { calculateOpportunityScore, calculateRiskScore } from '../services/analytics';
import type { CountryOverview } from '../types/country';
import type { OpportunityScoreResult } from '../services/analytics/opportunityScore';
import type { RiskScoreResult } from '../services/analytics/riskScore';

export interface UseCountryDataResult {
  data: CountryOverview | null;
  opportunityScore: OpportunityScoreResult | null;
  riskScore: RiskScoreResult | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  lastUpdated: Date | null;
  dataSource: 'live' | 'cached' | 'static' | null;
}

export const useCountryData = (countryCode: string = 'UZB'): UseCountryDataResult => {
  const [data, setData] = useState<CountryOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const overview = await dataService.getCountryOverview(countryCode);
      setData(overview);
      setLastUpdated(overview.lastUpdated);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch country data';
      setError(errorMessage);
      console.error('useCountryData error:', err);
    } finally {
      setLoading(false);
    }
  }, [countryCode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calculate scores when data changes
  const opportunityScore = useMemo(() => {
    if (!data) return null;
    return calculateOpportunityScore(data);
  }, [data]);

  const riskScore = useMemo(() => {
    if (!data) return null;
    return calculateRiskScore(data);
  }, [data]);

  return {
    data,
    opportunityScore,
    riskScore,
    loading,
    error,
    refresh: fetchData,
    lastUpdated,
    dataSource: data?.dataSource ?? null,
  };
};

export default useCountryData;
